import { createContext, useContext, useState, useEffect } from "react";
import { tryRefreshToken, isTokenExpired } from "../services/api";

const AuthContext = createContext({});

const BASE = "https://ginbecc-backend.onrender.com/api/v1";

export const tokenStorage = {
  get:        ()  => localStorage.getItem("token"),
  set:        (t) => localStorage.setItem("token", t),
  getRefresh: ()  => localStorage.getItem("refreshToken"),
  setRefresh: (t) => localStorage.setItem("refreshToken", t),
  getUser:    ()  => {
    try { return JSON.parse(localStorage.getItem("user") || "null"); }
    catch { return null; }
  },
  setUser:    (u) => localStorage.setItem("user", JSON.stringify(u)),
  clear:      ()  => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
  },
};

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);
  // In-memory only, so ForcePasswordModal can skip asking for it again.
  const [pendingPassword, setPendingPassword] = useState(null);

  useEffect(() => {
    const token = tokenStorage.get();
    const rt    = tokenStorage.getRefresh();
    const saved = tokenStorage.getUser();

    if (saved && (token || rt)) {
      setUser(saved);
      if (rt) {
        tryRefreshToken().then((ok) => {
          if (!ok && (!token || isTokenExpired(token))) {
            tokenStorage.clear();
            setUser(null);
          }
        });
      }
    }
    setLoading(false);

    const onLogout = () => {
      tokenStorage.clear();
      setUser(null);
    };
    window.addEventListener("auth:logout", onLogout);
    return () => window.removeEventListener("auth:logout", onLogout);
  }, []);

  const login = async (email, password) => {
    const res = await fetch(`${BASE}/auth/login`, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({ email, password }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || "ចូលប្រព័ន្ធបរាជ័យ");
    }

    const json = await res.json();
    const data = json.data;

    tokenStorage.set(data.accessToken);
    tokenStorage.setRefresh(data.refreshToken);

    const mustSetPassword = data.mustChangePassword === true;

    const userObj = {
      id:          data.userId,
      userNameKh:  data.userNameKh,
      userNameEn:  data.userNameEn,
      email:       data.email,
      roleName:    data.roleName,
      permissions: data.permissions || [],
      mustSetPassword,
    };
    tokenStorage.setUser(userObj);
    setUser(userObj);
    setPendingPassword(mustSetPassword ? password : null);
    return userObj;
  };

  const completePasswordSetup = () => {
    setPendingPassword(null);
    setUser(prev => {
      if (!prev) return prev;
      const next = { ...prev, mustSetPassword: false };
      tokenStorage.setUser(next);
      return next;
    });
  };

  const logout = async () => {
    try {
      const token = tokenStorage.get();
      if (token) {
        await fetch(`${BASE}/auth/logout`, {
          method:  "POST",
          headers: { "Authorization": `Bearer ${token}` },
        });
      }
    } catch {}
    tokenStorage.clear();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, completePasswordSetup, pendingPassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext) ?? {};
}